import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getCloudPrepperBaseUrl, DEV_AUTH_BYPASS_ENABLED, DEV_AUTH_BYPASS_ROLE, DEV_AUTH_BYPASS_TOKEN } from '../config/env';
import { safeLocalStorage } from '../utils/storage';
import { clearQueryCacheForSession, persistQueryCacheNow } from '../data/queryClient';

interface User {
    id: number;
    username: string;
    email: string;
    role: 'user' | 'admin';
    createdAt?: string;
    lastLogin?: string;
}

interface AuthContextType {
    user: User | null;
    token: string | null;
    loading: boolean;
    login: (email: string, password: string) => Promise<void>;
    register: (username: string, email: string, password: string) => Promise<void>;
    logout: () => void;
    isAuthenticated: boolean;
    isAdmin: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const AUTH_TOKEN_KEY = 'auth_token';
const AUTH_USER_KEY = 'auth_user';
export const AUTH_SESSION_EXPIRED_EVENT = 'auth:session-expired';

const persistAuth = (authToken: string, userData: User) => {
    // Prefer durable localStorage so refresh / new-tab restore still works if sessionStorage fails.
    safeLocalStorage.setItem(AUTH_TOKEN_KEY, authToken);
    safeLocalStorage.setItem(AUTH_USER_KEY, JSON.stringify(userData));
    try {
        sessionStorage.setItem(AUTH_TOKEN_KEY, authToken);
        sessionStorage.setItem(AUTH_USER_KEY, JSON.stringify(userData));
    } catch {
        // sessionStorage can throw in some browsers; localStorage copy is enough to restore.
    }
};

const clearPersistedAuth = () => {
    try {
        sessionStorage.removeItem(AUTH_TOKEN_KEY);
        sessionStorage.removeItem(AUTH_USER_KEY);
    } catch {
        // Ignore.
    }
    safeLocalStorage.removeItem(AUTH_TOKEN_KEY);
    safeLocalStorage.removeItem(AUTH_USER_KEY);
};

const readPersistedAuth = (): { token: string; user: User } | null => {
    let token: string | null = null;
    let userRaw: string | null = null;
    try {
        token = sessionStorage.getItem(AUTH_TOKEN_KEY);
        userRaw = sessionStorage.getItem(AUTH_USER_KEY);
    } catch {
        // Fall through to localStorage.
    }
    token = token ?? safeLocalStorage.getItem(AUTH_TOKEN_KEY);
    userRaw = userRaw ?? safeLocalStorage.getItem(AUTH_USER_KEY);
    if (!token || !userRaw) return null;

    try {
        return { token, user: JSON.parse(userRaw) as User };
    } catch {
        clearPersistedAuth();
        return null;
    }
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [token, setToken] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);

    const logout = useCallback(() => {

        setUser(null);
        setToken(null);
        clearPersistedAuth();
        clearQueryCacheForSession();
        try {
            sessionStorage.removeItem('cloudPrepQuizState');
        } catch {
            // Ignore storage errors during logout cleanup.
        }
        console.log('🚪 Logout: Cleared all session data');
    }, []);

    const verifyToken = useCallback(async (authToken: string): Promise<boolean> => {
        if (DEV_AUTH_BYPASS_ENABLED) return true;

        try {
            const url = `${getCloudPrepperBaseUrl()}/api/auth/verify`;

            const response = await fetch(url, {
                headers: {
                    Authorization: `Bearer ${authToken}`,
                },
            });

            if (response.status === 401 || response.status === 403) {
                return false;
            }

            if (!response.ok) {
                console.warn('Token verify unavailable, keeping cached session');
                return true;
            }

            const contentType = response.headers.get('content-type');
            if (!contentType?.includes('application/json')) {
                console.warn('Token verify returned non-JSON, keeping cached session');
                return true;
            }

            const data = await response.json();
            if (data.success && data.user) {
                setUser(data.user);
                persistAuth(authToken, data.user);
                return true;
            }

            return false;
        } catch (error) {
            console.warn('Token verify network error, keeping cached session:', error);
            return true;
        }
    }, []);

    useEffect(() => {
        const bootstrapAuth = async () => {
            if (DEV_AUTH_BYPASS_ENABLED) {
                const devToken = DEV_AUTH_BYPASS_TOKEN || 'dev-bypass';
                const devUser: User = {
                    id: 0,
                    username: 'dev',
                    email: 'dev@local',
                    role: DEV_AUTH_BYPASS_ROLE,
                };

                setToken(devToken);
                setUser(devUser);
                persistAuth(devToken, devUser);
                setLoading(false);
                return;
            }

            const storedAuth = readPersistedAuth();
            if (!storedAuth) {
                setLoading(false);
                return;
            }

            setToken(storedAuth.token);
            setUser(storedAuth.user);
            persistAuth(storedAuth.token, storedAuth.user);

            const isValid = await verifyToken(storedAuth.token);
            if (!isValid) {
                logout();
            }

            setLoading(false);
        };

        void bootstrapAuth();
    }, [logout, verifyToken]);

    useEffect(() => {
        const handleSessionExpired = () => logout();
        window.addEventListener(AUTH_SESSION_EXPIRED_EVENT, handleSessionExpired);
        return () => window.removeEventListener(AUTH_SESSION_EXPIRED_EVENT, handleSessionExpired);
    }, [logout]);

    const login = async (email: string, password: string): Promise<void> => {
        try {
            const url = `${getCloudPrepperBaseUrl()}/api/auth/login`;
            console.log('Attempting login to:', url);

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email, password }),
            });

            console.log('Login response status:', response.status, response.statusText);

            if (response.status === 404) {
                throw new Error(
                    'Authentication endpoint not found. The backend server may not have authentication endpoints implemented. ' +
                    `Please ensure the backend at ${getCloudPrepperBaseUrl()} has the /api/auth/login endpoint.`
                );
            }

            const contentType = response.headers.get('content-type');
            let data;

            if (contentType && contentType.includes('application/json')) {
                data = await response.json();
            } else {
                const text = await response.text();
                console.error('Non-JSON response:', text);
                if (response.status === 404) {
                    throw new Error(
                        'Authentication endpoint not found. The backend server may not have authentication endpoints implemented. ' +
                        `Please ensure the backend at ${getCloudPrepperBaseUrl()} has the /api/auth/login endpoint.`
                    );
                }
                throw new Error(`Server error: ${response.status} ${response.statusText}`);
            }

            if (!response.ok) {
                throw new Error(data.message || data.error || `Login failed: ${response.status} ${response.statusText}`);
            }

            if (!data.success) {
                throw new Error(data.message || data.error || 'Login failed');
            }

            const authToken = data.token;
            const userData = data.user;

            if (!authToken || !userData) {
                throw new Error('Invalid response: missing token or user data');
            }

            setToken(authToken);
            setUser(userData);
            persistAuth(authToken, userData);
            // Auth keys are in storage — allow React Query persistence for this session.
            persistQueryCacheNow();

            console.log('Login successful for user:', userData.email);
        } catch (error) {
            console.error('Login error:', error);
            if (error instanceof TypeError && error.message.includes('fetch')) {
                throw new Error('Cannot connect to server. Please check if the backend is running.');
            }
            throw error;
        }
    };

    const register = async (username: string, email: string, password: string): Promise<void> => {
        try {
            const url = `${getCloudPrepperBaseUrl()}/api/auth/register`;
            console.log('Attempting registration to:', url);

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ username, email, password }),
            });

            console.log('Registration response status:', response.status, response.statusText);

            if (response.status === 404) {
                throw new Error(
                    'Registration endpoint not found. The backend server may not have authentication endpoints implemented. ' +
                    `Please ensure the backend at ${getCloudPrepperBaseUrl()} has the /api/auth/register endpoint.`
                );
            }

            const contentType = response.headers.get('content-type');
            let data;

            if (contentType && contentType.includes('application/json')) {
                data = await response.json();
            } else {
                const text = await response.text();
                console.error('Non-JSON response:', text);
                if (response.status === 404) {
                    throw new Error(
                        'Registration endpoint not found. The backend server may not have authentication endpoints implemented. ' +
                        `Please ensure the backend at ${getCloudPrepperBaseUrl()} has the /api/auth/register endpoint.`
                    );
                }
                throw new Error(`Server error: ${response.status} ${response.statusText}`);
            }

            if (!response.ok) {
                throw new Error(data.message || data.error || `Registration failed: ${response.status} ${response.statusText}`);
            }

            if (!data.success) {
                throw new Error(data.message || data.error || 'Registration failed');
            }


            const authToken = data.token;
            const userData = data.user;

            if (!authToken || !userData) {
                throw new Error('Invalid response: missing token or user data');
            }

            setToken(authToken);
            setUser(userData);
            persistAuth(authToken, userData);
            persistQueryCacheNow();

            console.log('Registration successful for user:', userData.email);
        } catch (error) {
            console.error('Registration error:', error);
            if (error instanceof TypeError && error.message.includes('fetch')) {
                throw new Error('Cannot connect to server. Please check if the backend is running.');
            }
            throw error;
        }
    };

    const value: AuthContextType = {
        user,
        token,
        loading,
        login,
        register,
        logout,
        isAuthenticated: !!user && !!token,
        isAdmin: user?.role === 'admin',
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
